import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Divider,
  Switch,
  FormControlLabel,
  InputAdornment,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { Icon } from "@mui/material";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import invoiceService from "../../services/invoice.service";
import { useAuth } from "../../../auth/hooks/useAuth";
import {
  CreateInvoiceRequest,
  InvoiceAutofillResponse,
  GetApprovedGRNsResponse,
  PurchaseInvoice,
  InvoiceItem,
  VendorDetails,
  GRNReference,
  ApprovedGRN,
} from "../../types/invoice.types";
import { getBusinessId } from "../../services/invoice.service";

interface InvoiceFormProps {
  invoiceId?: string;
  grnId?: string; // Pre-selected GRN ID
  onSuccess?: (invoice: PurchaseInvoice) => void;
  onCancel?: () => void;
}

const InvoiceForm: React.FC<InvoiceFormProps> = ({
  invoiceId,
  grnId: preSelectedGrnId,
  onSuccess,
  onCancel,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const params = useParams();

  // Form state
  const [selectedGrnId, setSelectedGrnId] = useState<string>(
    preSelectedGrnId || ""
  );
  const [invoiceNumber, setInvoiceNumber] = useState<string>("");
  const [invoiceDate, setInvoiceDate] = useState<Dayjs>(dayjs().startOf("day"));
  const [dueDate, setDueDate] = useState<Dayjs | null>(null);
  const [globalTaxRate, setGlobalTaxRate] = useState<number>(0);
  const [taxInclusive, setTaxInclusive] = useState<boolean>(false);
  const [globalDiscountType, setGlobalDiscountType] = useState<
    "percentage" | "amount"
  >("percentage");
  const [globalDiscountPercentage, setGlobalDiscountPercentage] =
    useState<number>(0);
  const [globalDiscountAmount, setGlobalDiscountAmount] = useState<number>(0);
  const [paymentTerms, setPaymentTerms] = useState<string>("");
  const [internalNotes, setInternalNotes] = useState<string>("");
  const [vendorInvoiceNumber, setVendorInvoiceNumber] = useState<string>("");
  const [currency, setCurrency] = useState<string>("USD");
  const [exchangeRate, setExchangeRate] = useState<number>(1);

  // Data state
  const [availableGrns, setAvailableGrns] = useState<
    GetApprovedGRNsResponse["grns"]
  >([]);
  const [invoiceItems, setInvoiceItems] = useState<InvoiceItem[]>([]);
  const [vendorDetails, setVendorDetails] = useState<VendorDetails | null>(
    null
  );
  const [grnReference, setGrnReference] = useState<GRNReference | null>(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingGrnData, setLoadingGrnData] = useState(false);
  const [grnSelectionOpen, setGrnSelectionOpen] = useState(false);
  const [validatingInvoiceNumber, setValidatingInvoiceNumber] = useState(false);
  const [invoiceNumberError, setInvoiceNumberError] = useState<string>("");

  // Calculated totals
  const [calculatedTotals, setCalculatedTotals] = useState({
    subtotal: 0,
    totalTaxAmount: 0,
    totalDiscountAmount: 0,
    grandTotal: 0,
  });

  const getBusinessIdForForm = () =>
    getBusinessId(user, vendorDetails, grnReference);

  // Load available GRNs
  const loadAvailableGrns = useCallback(async () => {
    try {
      const response = await invoiceService.getApprovedGRNs({
        business_id: getBusinessIdForForm(),
        params: {
          page: 1,
          limit: 100,
        },
      });

      if (response.success) {
        setAvailableGrns(
          (response.data.grns || [])
            .map((grn) => {
              const grn_id = grn.grn_id || grn._id;
              if (!grn_id) return null;
              return { ...grn, grn_id: String(grn_id) };
            })
            .filter(Boolean) as ApprovedGRN[]
        );
      }
    } catch (error) {
      console.error("Error loading available GRNs:", error);
      toast.error("Failed to load available GRNs");
    }
  }, [user]);

  // Load GRN data for auto-fill
  const loadGrnData = useCallback(
    async (grnId: string) => {
      if (!grnId) return;
      setLoadingGrnData(true);
      try {
        const response = await invoiceService.getInvoiceAutofillData({
          business_id: getBusinessIdForForm(),
          grn_id: grnId,
        });
        if (response.success) {
          const data: InvoiceAutofillResponse = response.data;

          console.log(
            "[DEBUG] Raw vendor data from backend:",
            data.vendor_details
          );

          // Ensure vendor details are properly set
          const vendorDetailsData = {
            ...data.vendor_details,
            vendor_id:
              data.vendor_details.vendor_id ||
              data.vendor_details.supplier_id ||
              "",
            vendor_name:
              data.vendor_details.vendor_name ||
              data.vendor_details.supplier_name ||
              data.vendor_details.name ||
              data.vendor_details.business_name ||
              "Unknown Vendor", // Fallback name
          };

          console.log("[DEBUG] Processed vendor details:", vendorDetailsData);

          // Ensure items have grn_item_id
          const validatedItems = data.items.map((item) => {
            if (!item.grn_item_id) {
              console.error("[DEBUG] Item missing grn_item_id:", item);
              // Try to use item_id as fallback if grn_item_id is missing
              return {
                ...item,
                grn_item_id: item.item_id || item._id || "",
              };
            }
            return item;
          });

          console.log("[DEBUG] Loaded GRN data:", {
            grnId,
            vendorDetails: vendorDetailsData,
            grnReference: data.grn_reference || data.grn_details,
            items: validatedItems,
            itemsWithGrnItemId: validatedItems.filter(
              (item) => item.grn_item_id
            ),
            itemsWithoutGrnItemId: validatedItems.filter(
              (item) => !item.grn_item_id
            ),
          });

          setVendorDetails(vendorDetailsData);
          setGrnReference(data.grn_reference || data.grn_details);
          setCurrency(data.currency);
          setInvoiceNumber(data.suggested_invoice_number);
          setInvoiceItems(validatedItems);

          toast.success("GRN data loaded successfully");
        }
      } catch (error: any) {
        console.error("Error loading GRN data:", error);
        toast.error(error.message || "Failed to load GRN data");
      } finally {
        setLoadingGrnData(false);
      }
    },
    [user]
  );

  // Validate invoice number
  const validateInvoiceNumber = useCallback(
    async (invoiceNum: string | undefined) => {
      if (!invoiceNum?.trim() || !vendorDetails?.vendor_id) return;

      setValidatingInvoiceNumber(true);
      setInvoiceNumberError("");

      try {
        const response = await invoiceService.validateInvoiceNumber({
          business_id: getBusinessIdForForm(),
          vendor_id: vendorDetails.vendor_id,
          invoice_number: invoiceNum,
          exclude_invoice_id: invoiceId,
        });

        if (response.success) {
          if (!response.data.is_valid) {
            setInvoiceNumberError(response.data.message);
          }
        }
      } catch (error: any) {
        console.error("Error validating invoice number:", error);
        setInvoiceNumberError("Failed to validate invoice number");
      } finally {
        setValidatingInvoiceNumber(false);
      }
    },
    [vendorDetails, invoiceId, user]
  );

  // Calculate totals
  const calculateTotals = useCallback(() => {
    const totals = invoiceService.calculateInvoiceTotals(
      invoiceItems,
      globalTaxRate,
      globalDiscountType === "percentage" ? globalDiscountPercentage : 0,
      globalDiscountType === "amount" ? globalDiscountAmount : 0,
      taxInclusive
    );
    setCalculatedTotals(totals);
  }, [
    invoiceItems,
    globalTaxRate,
    globalDiscountType,
    globalDiscountPercentage,
    globalDiscountAmount,
    taxInclusive,
  ]);

  // Update item
  const updateItem = (index: number, updates: Partial<InvoiceItem>) => {
    const updatedItems = [...invoiceItems];
    updatedItems[index] = { ...updatedItems[index], ...updates };

    // Recalculate line total
    const item = updatedItems[index];
    const lineTotal = item.quantity_invoiced * item.unit_price;
    item.total_price = lineTotal;

    // Calculate discount
    let discountAmount = 0;
    if (item.discount_percentage && item.discount_percentage > 0) {
      discountAmount = (lineTotal * item.discount_percentage) / 100;
    } else if (item.discount_amount && item.discount_amount > 0) {
      discountAmount = item.discount_amount;
    }

    // Calculate tax
    const taxableAmount = lineTotal - discountAmount;
    let taxAmount = 0;
    if (item.tax_rate && item.tax_rate > 0) {
      if (taxInclusive) {
        taxAmount = (taxableAmount * item.tax_rate) / (100 + item.tax_rate);
      } else {
        taxAmount = (taxableAmount * item.tax_rate) / 100;
      }
    }

    item.tax_amount = taxAmount;
    item.line_total = taxInclusive ? taxableAmount : taxableAmount + taxAmount;

    setInvoiceItems(updatedItems);
  };

  // Apply tax to all items
  const applyTaxToAllItems = () => {
    const updatedItems = invoiceItems.map((item: InvoiceItem) => ({
      ...item,
      tax_rate: globalTaxRate,
    }));
    setInvoiceItems(updatedItems);
  };

  // Handle submit
  const handleSubmit = async () => {
    if (!vendorDetails || !grnReference) {
      toast.error("Please select a GRN first");
      return;
    }

    if (invoiceNumberError) {
      toast.error("Please fix the invoice number validation error");
      return;
    }

    // Validate required fields
    const businessId = getBusinessIdForForm();
    if (!businessId) {
      toast.error("Business ID is required. Please check your session.");
      return;
    }

    if (!invoiceNumber?.trim()) {
      toast.error("Invoice number is required");
      return;
    }

    if (!vendorDetails.vendor_name?.trim()) {
      toast.error("Vendor name is required");
      return;
    }

    if (invoiceItems.length === 0) {
      toast.error("At least one item is required");
      return;
    }

    // Validate that all items have grn_item_id
    const invalidItems = invoiceItems.filter((item) => !item.grn_item_id);
    if (invalidItems.length > 0) {
      toast.error("All items must have a valid GRN item ID");
      return;
    }

    // Validate invoice date is not in the future
    const now = dayjs();
    if (invoiceDate.isAfter(now)) {
      toast.error("Invoice date cannot be in the future");
      return;
    }

    setSaving(true);
    try {
      const invoiceData: CreateInvoiceRequest = {
        business_id: businessId,
        invoice_data: {
          invoice_number: invoiceNumber.trim(),
          invoice_date: invoiceDate.valueOf(),
          due_date: dueDate?.valueOf(),
          vendor_details: {
            ...vendorDetails,
            vendor_id:
              vendorDetails.vendor_id || vendorDetails.supplier_id || "",
            vendor_name:
              vendorDetails.vendor_name ||
              vendorDetails.supplier_name ||
              vendorDetails.name ||
              vendorDetails.business_name ||
              "Unknown Vendor",
          },
          grn_references: [grnReference],
          currency: currency || "USD",
          exchange_rate: exchangeRate,
          tax_inclusive: taxInclusive,
          global_tax_rate: globalTaxRate,
          global_discount_type: globalDiscountType,
          global_discount_percentage:
            globalDiscountType === "percentage"
              ? globalDiscountPercentage
              : undefined,
          global_discount_amount:
            globalDiscountType === "amount" ? globalDiscountAmount : undefined,
          items: invoiceItems,
          payment_terms: paymentTerms?.trim() || undefined,
          internal_notes: internalNotes?.trim() || undefined,
          vendor_invoice_number: vendorInvoiceNumber?.trim() || undefined,
          subtotal: calculatedTotals.subtotal,
          total_tax_amount: calculatedTotals.totalTaxAmount,
          total_discount_amount: calculatedTotals.totalDiscountAmount,
          grand_total: calculatedTotals.grandTotal,
        },
      };

      console.log("[DEBUG] Submitting invoice data:", invoiceData);

      const response = await invoiceService.createInvoice(invoiceData);

      if (response.success) {
        toast.success("Invoice created successfully!");

        // Navigate to invoice list or call success callback
        if (onSuccess) {
          // Note: We don't have the full invoice object in the response, so we'll navigate instead
          navigate("/business/procurement/invoices");
        } else {
          navigate("/business/procurement/invoices");
        }
      }
    } catch (error: any) {
      console.error("Error creating invoice:", error);
      toast.error(error.response?.data?.message || "Failed to create invoice");
    } finally {
      setSaving(false);
    }
  };

  // Load initial data
  useEffect(() => {
    setLoading(true);
    loadAvailableGrns().finally(() => setLoading(false));
  }, [loadAvailableGrns]);

  // Load GRN data when GRN is selected
  useEffect(() => {
    if (selectedGrnId) {
      loadGrnData(selectedGrnId);
    }
  }, [selectedGrnId, loadGrnData]);

  // Validate invoice number on change
  useEffect(() => {
    if (invoiceNumber) {
      const timeoutId = setTimeout(() => {
        validateInvoiceNumber(invoiceNumber);
      }, 500);

      return () => clearTimeout(timeoutId);
    }
    // Return undefined for all other code paths
    return undefined;
  }, [invoiceNumber, validateInvoiceNumber]);

  // Recalculate totals when items or discounts change
  useEffect(() => {
    calculateTotals();
  }, [calculateTotals]);

  // Add a useEffect to log whenever grnReference or vendorDetails changes
  useEffect(() => {
    console.log("[DEBUG] grnReference or vendorDetails changed:", {
      selectedGrnId,
      grnReference,
      vendorDetails,
    });
  }, [selectedGrnId, grnReference, vendorDetails]);

  const handleGrnSelect = (grn_id: string) => {
    setSelectedGrnId(grn_id);
    setTimeout(() => {
      console.log("[DEBUG] After GRN select:", {
        selectedGrnId: grn_id,
      });
    }, 0);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Create Purchase Invoice
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Generate an invoice from an approved Goods Receipt Note
        </Typography>

        {loading ? (
          <Box>
            <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={400} />
          </Box>
        ) : (
          <>
            {/* GRN Selection UI */}
            <Box sx={{ mb: 3 }}>
              {selectedGrnId && grnReference && vendorDetails ? (
                <Paper sx={{ p: 2, mb: 2, bgcolor: "grey.50" }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Selected GRN
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {grnReference.grn_number}
                  </Typography>
                  <Typography variant="body2">
                    <strong>PO:</strong> {grnReference.po_number} &nbsp;|&nbsp;
                    <strong>Vendor:</strong> {vendorDetails.vendor_name}
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => setGrnSelectionOpen(true)}
                    sx={{ mt: 1 }}
                  >
                    Change GRN
                  </Button>
                </Paper>
              ) : (
                <Button
                  variant="outlined"
                  onClick={() => setGrnSelectionOpen(true)}
                  startIcon={<Icon>search</Icon>}
                >
                  Select GRN
                </Button>
              )}
            </Box>

            {/* Invoice Details */}
            {selectedGrnId && (
              <>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Invoice Details
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Invoice Number"
                          value={invoiceNumber}
                          onChange={(e) => setInvoiceNumber(e.target.value)}
                          error={Boolean(invoiceNumberError)}
                          helperText={
                            invoiceNumberError || "Must be unique per vendor"
                          }
                          InputProps={{
                            endAdornment: validatingInvoiceNumber ? (
                              <InputAdornment position="end">
                                <Icon>hourglass_empty</Icon>
                              </InputAdornment>
                            ) : undefined,
                          }}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Vendor Invoice Number"
                          value={vendorInvoiceNumber}
                          onChange={(e) =>
                            setVendorInvoiceNumber(e.target.value)
                          }
                          helperText="Vendor's own invoice number (optional)"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <DatePicker
                          label="Invoice Date"
                          value={invoiceDate}
                          onChange={(newValue) =>
                            newValue && setInvoiceDate(newValue)
                          }
                          renderInput={(params) => (
                            <TextField {...params} fullWidth required />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <DatePicker
                          label="Due Date"
                          value={dueDate}
                          onChange={(newValue) => setDueDate(newValue)}
                          renderInput={(params) => (
                            <TextField {...params} fullWidth />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Global Tax Rate"
                          type="number"
                          value={globalTaxRate}
                          onChange={(e) =>
                            setGlobalTaxRate(Number(e.target.value))
                          }
                          InputProps={{
                            inputProps: { min: 0, max: 100, step: 0.01 },
                            endAdornment: (
                              <InputAdornment position="end">
                                <Button
                                  size="small"
                                  onClick={applyTaxToAllItems}
                                  disabled={invoiceItems.length === 0}
                                >
                                  Apply to All
                                </Button>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Payment Terms"
                          value={paymentTerms}
                          onChange={(e) => setPaymentTerms(e.target.value)}
                          placeholder="e.g., Net 30 days"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Internal Notes"
                          value={internalNotes}
                          onChange={(e) => setInternalNotes(e.target.value)}
                          multiline
                          rows={2}
                          placeholder="Internal notes about this invoice..."
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Invoice Items */}
                {loadingGrnData ? (
                  <Card>
                    <CardContent>
                      <Skeleton height={300} />
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    <Card sx={{ mb: 3 }}>
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 2,
                          }}
                        >
                          <Typography variant="h6">
                            Invoice Items ({invoiceItems.length})
                          </Typography>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={taxInclusive}
                                onChange={(e) =>
                                  setTaxInclusive(e.target.checked)
                                }
                              />
                            }
                            label="Tax Inclusive"
                          />
                        </Box>

                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Item</TableCell>
                                <TableCell align="right">Qty</TableCell>
                                <TableCell align="right">Unit Price</TableCell>
                                <TableCell align="right">Tax %</TableCell>
                                <TableCell align="right">Line Total</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {invoiceItems.map((item, index) => (
                                <TableRow key={item.item_id}>
                                  <TableCell>
                                    <Box>
                                      <Typography
                                        variant="body2"
                                        fontWeight="medium"
                                      >
                                        {item.item_name}
                                      </Typography>
                                      {item.item_description && (
                                        <Typography
                                          variant="caption"
                                          color="textSecondary"
                                        >
                                          {item.item_description}
                                        </Typography>
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell align="right">
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={item.quantity_invoiced}
                                      onChange={(e) =>
                                        updateItem(index, {
                                          quantity_invoiced: Number(
                                            e.target.value
                                          ),
                                        })
                                      }
                                      inputProps={{ min: 0, step: 0.01 }}
                                      sx={{ width: 80 }}
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={item.unit_price}
                                      onChange={(e) =>
                                        updateItem(index, {
                                          unit_price: Number(e.target.value),
                                        })
                                      }
                                      inputProps={{ min: 0, step: 0.01 }}
                                      sx={{ width: 100 }}
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={item.tax_rate}
                                      onChange={(e) =>
                                        updateItem(index, {
                                          tax_rate: Number(e.target.value),
                                        })
                                      }
                                      inputProps={{
                                        min: 0,
                                        max: 100,
                                        step: 0.01,
                                      }}
                                      sx={{ width: 80 }}
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2">
                                      {invoiceService.formatCurrency(
                                        item.line_total
                                      )}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>

                    {/* Global Discounts & Totals */}
                    <Card sx={{ mb: 3 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Discounts & Totals
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Global Discount %"
                              type="number"
                              value={globalDiscountPercentage}
                              onChange={(e) => {
                                setGlobalDiscountPercentage(
                                  Number(e.target.value)
                                );
                                setGlobalDiscountAmount(0); // Reset amount when % is used
                              }}
                              inputProps={{ min: 0, max: 100, step: 0.01 }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Global Discount Amount"
                              type="number"
                              value={globalDiscountAmount}
                              onChange={(e) => {
                                setGlobalDiscountAmount(Number(e.target.value));
                                setGlobalDiscountPercentage(0); // Reset % when amount is used
                              }}
                              inputProps={{ min: 0, step: 0.01 }}
                            />
                          </Grid>
                        </Grid>

                        <Divider sx={{ my: 3 }} />

                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="textSecondary">
                              Subtotal:
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sx={{ textAlign: "right" }}>
                            <Typography variant="body2">
                              {invoiceService.formatCurrency(
                                calculatedTotals.subtotal
                              )}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="textSecondary">
                              Total Discount:
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sx={{ textAlign: "right" }}>
                            <Typography variant="body2" color="error.main">
                              -
                              {invoiceService.formatCurrency(
                                calculatedTotals.totalDiscountAmount
                              )}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="textSecondary">
                              Total Tax:
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sx={{ textAlign: "right" }}>
                            <Typography variant="body2">
                              {invoiceService.formatCurrency(
                                calculatedTotals.totalTaxAmount
                              )}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="h6" fontWeight="bold">
                              Grand Total:
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sx={{ textAlign: "right" }}>
                            <Typography
                              variant="h6"
                              fontWeight="bold"
                              color="primary.main"
                            >
                              {invoiceService.formatCurrency(
                                calculatedTotals.grandTotal
                              )}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>

                    {/* Action Buttons */}
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        variant="outlined"
                        onClick={
                          onCancel ||
                          (() => navigate("/business/procurement/invoices"))
                        }
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                      <LoadingButton
                        variant="contained"
                        onClick={handleSubmit}
                        loading={saving}
                        disabled={
                          !selectedGrnId ||
                          invoiceItems.length === 0 ||
                          Boolean(invoiceNumberError)
                        }
                      >
                        Create Invoice
                      </LoadingButton>
                    </Box>
                  </>
                )}
              </>
            )}
          </>
        )}

        {/* GRN Selection Dialog */}
        <Dialog
          open={grnSelectionOpen}
          onClose={() => setGrnSelectionOpen(false)}
        >
          <DialogTitle>Select GRN for Invoice</DialogTitle>
          <DialogContent>
            {availableGrns.length === 0 ? (
              <Alert severity="info">
                No approved GRNs available for invoicing.
              </Alert>
            ) : (
              <List>
                {availableGrns.map((grn) => (
                  <ListItem
                    button
                    key={grn.grn_id}
                    onClick={() => {
                      handleGrnSelect(grn.grn_id);
                      setGrnSelectionOpen(false);
                    }}
                  >
                    <ListItemText
                      primary={grn.grn_number}
                      secondary={`PO: ${grn.po_number} | Vendor: ${grn.vendor_name} | Value: ${grn.total_value} ${grn.currency}`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGrnSelectionOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default InvoiceForm;
